import PropTypes from "prop-types";
import React, { Component } from "react";
import DatePicker from "material-ui/DatePicker";
import IconButton from "material-ui/IconButton";
import CloseIcon from "material-ui/svg-icons/navigation/close";
import theme from "styles/theme";
import Label from "./label";
import moment from "lib/moment-i18n";
import { Decorator as FormsyElement } from "formsy-react";
import { extractFormsyProps } from "lib/helpers";
import Globalize from "globalize";
import { DATATEST } from "constants/integration";
import { getLocale } from "lib/globalize-util";

const styles = {
    base: {
        display: "inline-block",
        position: "relative",
    },
    textField: {
        border: "1px solid " + theme.palette.lightGrey,
        borderRadius: "4px",
        height: "40px",
        width: "100%",
    },
    textFieldLarge: {
        height: "50px",
    },
    dateFieldSmall: {
        height: "40px",
        maxWidth: "220px",
    },
    input: {
        padding: "0 16px",
    },
    hint: {
        bottom: "50%",
        padding: "0 16px",
        transform: "translateY(50%)",
    },
    clear: {
        position: "absolute",
        top: "-2px",
        right: 0,
        zIndex: 2,
    },
    disabledInputStyle: {
        backgroundColor: "#F5F5F5",
        borderColor: "#E1E1E1",
        color: "#BDBDBD",
    },
};

/** @augments {Component<any, any>} */
@FormsyElement()
class FormDatePicker extends Component {
    static propTypes = {
        label: PropTypes.node,
        customLabel: PropTypes.node,
        style: PropTypes.object,
        textFieldStyle: PropTypes.object,
        large: PropTypes.bool, // height is bigger
        small: PropTypes.bool, // width is smaller
        value: PropTypes.instanceOf(moment),
        minDate: PropTypes.instanceOf(moment),
        maxDate: PropTypes.instanceOf(moment),
        onChange: PropTypes.func,
        utc: PropTypes.bool,
        name: PropTypes.string,
        key: PropTypes.string,
        clearable: PropTypes.bool,
        clearStyle: PropTypes.object,
        dataTest: PropTypes.string,
    };

    static defaultProps = {
        style: {},
        textFieldStyle: {},
        labelStyle: {},
        large: false,
        small: false,
        utc: true,
        clearable: false,
        name: "datepicker",
    };

    constructor(props) {
        super(props);
        this.handleChange = this.handleChange.bind(this);
        this.handleClear = this.handleClear.bind(this);
    }

    handleChange(e, date) {
        const { onChange, utc } = this.props;
        if (onChange) {
            onChange(e, utc ? moment.utc(moment(date).format("YYYY-MM-DD")) : moment(date));
        }
    }

    handleClear(e) {
        const { onChange } = this.props;
        if (onChange) {
            onChange(e);
        }
    }

    formatDate(date) {
        return moment(date).locale(getLocale()).format("MMM D, YYYY");
    }

    // Material UI expects a date object. Also account for utc
    convertMomentToDate(value) {
        return value ? moment(value.format("YYYY-MM-DD")).toDate() : undefined;
    }

    render() {
        /* eslint-disable @typescript-eslint/no-unused-vars */
        let {
            // eslint-disable-next-line prefer-const
            utc, // not used on purpose
            // eslint-disable-next-line prefer-const
            label,
            // eslint-disable-next-line prefer-const
            labelStyle,
            // eslint-disable-next-line prefer-const
            customLabel,
            // eslint-disable-next-line prefer-const
            large,
            // eslint-disable-next-line prefer-const
            small,
            // eslint-disable-next-line prefer-const
            style,
            // eslint-disable-next-line prefer-const
            textFieldStyle,
            // eslint-disable-next-line prefer-const
            inputStyle,
            // eslint-disable-next-line prefer-const
            value,
            // eslint-disable-next-line prefer-const
            minDate,
            // eslint-disable-next-line prefer-const
            maxDate,
            // eslint-disable-next-line prefer-const
            dataTest,
            // eslint-disable-next-line prefer-const
            adornment,
            // eslint-disable-next-line prefer-const
            clearable,
            // eslint-disable-next-line prefer-const
            clearStyle = {},
            // eslint-disable-next-line prefer-const
            labelClassName,
            // eslint-disable-next-line prefer-const
            key,
            disabled, // eslint-disable-line prefer-const
            ...other
        } = this.props;
        other = extractFormsyProps(other);
        /* eslint-enable no-unused-vars */

        const date = this.convertMomentToDate(value);
        const min = this.convertMomentToDate(minDate);
        const max = this.convertMomentToDate(maxDate);

        return (
            <div style={Object.assign({}, styles.base, style)} data-test={dataTest}>
                {customLabel || (
                    <Label style={labelStyle} className={labelClassName}>
                        {label}
                    </Label>
                )}
                <DatePicker
                    autoOk={true}
                    {...other}
                    key={key || name}
                    value={date}
                    minDate={min}
                    maxDate={max}
                    onChange={this.handleChange}
                    underlineShow={false}
                    inputStyle={Object.assign({}, styles.input, inputStyle, disabled && styles.disabledInputStyle)}
                    textFieldStyle={Object.assign(
                        {},
                        styles.textField,
                        large && styles.textFieldLarge,
                        small && styles.dateFieldSmall,
                        textFieldStyle
                    )}
                    hintStyle={styles.hint}
                    formatDate={this.formatDate}
                    name={name || label}
                    okLabel={Globalize.formatMessage("Ok")}
                    cancelLabel={
                        <span data-test={DATATEST.CALENDAR_CANCEL_BUTTON}>{Globalize.formatMessage("Cancel")}</span>
                    }
                    disabled={disabled}
                />
                {adornment}
                {clearable && value ? (
                    <IconButton
                        data-test={DATATEST.GALLERY.CLEAR_DATES}
                        style={Object.assign({}, styles.clear, clearStyle)}
                        onClick={this.handleClear}
                    >
                        <CloseIcon />
                    </IconButton>
                ) : null}
            </div>
        );
    }
}

export default FormDatePicker;
